import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { BehaviorSubject, catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { hostName, openBaseUrl } from 'app/app.config';

import { LocalStorageAuth } from 'app/core/auth/auth.model';
import { signIn, SignInInput, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { ApiService } from 'app/modules/services/api.services';
import { FirebaseMessagingService } from 'app/core/services/firebase-messaging.service';

enum ERROR {
    INVALID_CREDENTIALS = 'Invalid Email/Mobile or Password.',
    PASSWORD_MISSMATCH = 'Both password fields should be same'
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _authState$ = new BehaviorSubject<boolean>(false);
    authState$ = this._authState$.asObservable();
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _apiService = inject(ApiService);
    private _firebaseMessaging = inject(FirebaseMessagingService, { optional: true });

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(data: LocalStorageAuth) {
        localStorage.setItem('d', btoa(JSON.stringify(data)));
    }

    get accessToken(): LocalStorageAuth {
        return AuthUtils.getLocalStorage();
    }

    set urls(data: any) {
        if (data && data.length) {
            const urlObj = {};
            data.forEach((u) => {
                urlObj[u.key_name] = u.value_name;
            });
            localStorage.setItem('urls', btoa(JSON.stringify(urlObj)));
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    async signIn(credentials: { email?: string; mobile?: string; password: string }): Promise<Observable<any>> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        // Determine username from either email or mobile
        let username = credentials.email || credentials.mobile;

        if (!username) {
            return of({
                appearance: 'outline',
                content: ERROR.INVALID_CREDENTIALS,
                shake: true,
                showIcon: false,
                type: 'error',
            });
        }

        // Add +91 prefix if it's a mobile number (10 digits)
        if (/^[0-9]{10}$/.test(username)) {
            username = `+91${username}`;
        }

        const SignInInput: SignInInput = {
            username: username.toString(),
            password: credentials.password.toString(),
            options: { authFlowType: 'USER_PASSWORD_AUTH' }
        };

        try {
            await signOut();
            let loginResponse = await signIn(SignInInput);
            switch (loginResponse.nextStep.signInStep) {
                case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                    console.log('The user was created with a temporary password and must set a new one. Complete the process with confirmSignIn');
                    break;
                case 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE':
                    console.log('The sign-in must be confirmed with a custom challenge response. Complete the process with confirmSignIn');
                    break;
                case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
                    console.log('The sign-in must be confirmed with a TOTP code from the user. Complete the process with confirmSignIn');
                    break;
                case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
                    console.log('he TOTP setup process must be continued. Complete the process with confirmSignIn');
                    break;
                case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
                    console.log('The sign-in must be confirmed with a SMS code from the user. Complete the process with confirmSignIn');
                    break;
                case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
                    console.log('The user must select their mode of MFA verification before signing in. Complete the process with confirmSignIn');
                    break;
                case 'RESET_PASSWORD':
                    console.log('The user must reset their password via resetPassword');
                    break;
                case 'CONFIRM_SIGN_UP':
                    console.log('The user hasnt completed the sign-up flow fully and must be confirmed via confirmSignUp');
                    break;
                case 'DONE':
                    const s = await fetchAuthSession();
                    let userString = JSON.stringify({
                        auth_token: s.tokens.idToken.toString(),
                        customer_id: s.tokens.idToken.payload['custom:c1_customer_id'],
                    });

                    const t = s.tokens.idToken.toString();
                    const u = s.tokens.idToken.payload['custom:c1_customer_id'].toString();
                    this.accessToken = {
                        t: t,
                        u: t
                    };
                    this.setAuthState(true);

                    this.sendNotificationToken(userString);
                    return of(true);
                default:
                    console.log('DONE');
                    break;
            };

        } catch (error) {
            console.log(error, 'error');

            return of({
                appearance: 'outline',
                content: ERROR.INVALID_CREDENTIALS,
                shake: true,
                showIcon: false,
                type: 'error',
            });
        }
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
       
                // Set the authenticated flag to true
                this.setAuthState(true);
                const user = {
                    id    : 'cfaad35d-07a3-4447-a6c3-d8c3d54fd5df',
                    name  : 'Brian Hughes',
                    email : 'hughes.brian@company.com',
                    avatar: 'assets/images/avatars/brian-hughes.jpg',
                    status: 'online',
                };
                // Store the user on the user service
                this._userService.user = user;

                // Return true
                return of(true);
          
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        console.log("signing out");
        
        // Clear FCM token
        if (this._firebaseMessaging) {
            this._firebaseMessaging.clearToken();
        }
        
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('d');
        localStorage.removeItem('fcm_token_saved_token');
        

        // Set the authenticated flag to false
        this.setAuthState(false);

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            if (!this._authState$.value) {
                this._authState$.next(true);
            }
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken || !this.accessToken.t || !this.accessToken.u) {
            this.setAuthState(false);
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken.t)) {
            this.setAuthState(false);
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    public getOrgDetails(): Observable<any> {
        return this._httpClient.get<any>(
            `${openBaseUrl}/tenantmaster/details?url=https://${hostName}`,
            {},
        );
    }

    public getSettingByDeviceType(): Observable<any> {
        return this._httpClient.get<any>(
            `${openBaseUrl}/openapis/settings/by-device-type?url=https://${hostName}&device=admin`,
            {},
        );
    }
    async sendNotificationToken(userString){
        // Wait for Firebase messaging to be initialized and get the token
        // Try multiple times with delays to handle async initialization
        let attempts = 0;
        const maxAttempts = 10;
        const delayMs = 500;

        while (attempts < maxAttempts) {
            try {
                // Get token from FirebaseMessagingService
                if (this._firebaseMessaging) {
                    const token = await this._firebaseMessaging.getCurrentToken();
                    
                    if (token) {
                        // Store token in 'notiToken' for backward compatibility with API service
                        localStorage.setItem('notiToken', token);
                        
                        // Send token to backend
                        this._apiService
                            .sendNotifictoken(token, userString)
                            .subscribe((data) => {
                                console.log('Notification token sent successfully');
                            }, error => {
                                console.log('Error sending notification token:', error);
                                if(error.status === 500){
                                    // Retry after delay
                                    setTimeout(() => { this.sendNotificationToken(userString); }, 5000);
                                }     
                            });
                        return; // Success, exit the retry loop
                    }
                }
            } catch (error) {
                console.log('Error getting FCM token, attempt', attempts + 1, error);
            }

            // Wait before next attempt
            attempts++;
            if (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, delayMs));
            }
        }

        console.warn('Could not get FCM token after', maxAttempts, 'attempts. Token will be sent when available.');
    }

    private setAuthState(authenticated: boolean): void
    {
        this._authenticated = authenticated;
        if (this._authState$.value !== authenticated)
        {
            this._authState$.next(authenticated);
        }
    }
}
